import { createFileRoute } from "@tanstack/react-router";
import SobreNosotros from "@/pages/SobreNosotros";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

const canonicalUrl = "https://agenciaseoevolution.com/sobre-nosotros";

const faqs = [
  {
      question: "¿Por qué SEOEVOLUTION es diferente a otras agencias SEO?",
      answer: "Somos la primera agencia premium en España especializada 100% en SEO potenciado con Inteligencia Artificial. Además, ofrecemos zonas exclusivas blindadas por contrato para proteger tu inversión: no trabajamos con tu competencia directa. Con más de 20 años de experiencia y resultados verificables, no prometemos posiciones — ejecutamos el trabajo profesional que las consigue."
    },
    {
      question: "¿Qué significa que sois partners de Shopify y Lovable?",
      answer: "Como Shopify Partners oficiales, tenemos acceso a recursos avanzados, soporte prioritario y certificaciones que garantizan tiendas online de máxima calidad. Con Lovable, utilizamos la plataforma más innovadora para crear sitios web ultrarrápidos con React, optimizados para SEO desde el código base."
    },
    {
      question: "¿Cómo puede la IA mejorar mi posicionamiento SEO?",
      answer: "La IA nos permite analizar millones de datos en segundos, detectar patrones de búsqueda emergentes, predecir cambios algorítmicos de Google, optimizar contenido de forma más precisa y automatizar tareas repetitivas para dedicar más tiempo a la estrategia. Es como tener un equipo de 50 analistas trabajando 24/7."
    },
    {
      question: "¿Trabajáis con cualquier tipo de negocio o sector?",
      answer: "Sí, trabajamos con todos los sectores: desde negocios locales (restaurantes, clínicas, despachos) hasta grandes e-commerce y empresas B2B. Nuestra metodología se adapta a cada industria. Lo único que exigimos es compromiso con la calidad y ganas de crecer."
    },
    {
      question: "¿Cuánto tiempo tarda en verse resultados con vuestro servicio?",
      answer: "Depende del proyecto y la competencia. En SEO local, se pueden ver mejoras en 4-8 semanas. En SEO nacional competitivo, entre 3-6 meses. Siempre establecemos hitos realistas en la auditoría inicial y monitorizamos el progreso semanalmente."
    },
    {
      question: "¿Qué compromiso mínimo tienen SEO local y GEO?",
      answer: "En SEO local el compromiso mínimo es de 3 meses y en GEO, de 6 meses. No son plazos garantizados de resultados. Al finalizar el mínimo acordado evaluamos el proyecto; cualquier continuidad posterior es opcional y a convenir. Los demás servicios mantienen los mínimos de su propuesta."
    }
  ];

const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "Sobre SEOEVOLUTION - Agencia SEO con IA",
  "description": "Conoce la historia, equipo, metodología y valores de SEOEVOLUTION, la primera agencia SEO potenciada con Inteligencia Artificial en España.",
  "url": canonicalUrl,
  "mainEntity": {
    "@id": "https://agenciaseoevolution.com/#organization"
  },
  "specialty": [
    "SEO potenciado con Inteligencia Artificial",
    "Optimización para Google Maps y Bing Maps",
    "Optimización de fichas de producto para e-commerce",
    "Desarrollo exclusivo en Shopify",
    "Desarrollo web con React y Lovable"
  ],
  "knowsAbout": [
    {
      "@type": "Thing",
      "name": "SEO con Inteligencia Artificial",
      "description": "Uso de algoritmos de IA para análisis predictivo, optimización de contenido y detección de oportunidades de posicionamiento"
    },
    {
      "@type": "Thing",
      "name": "Sistema de Zonas Exclusivas",
      "description": "Metodología única que garantiza que no trabajamos con competidores directos en la misma zona geográfica"
    },
    {
      "@type": "Thing",
      "name": "Optimización Local",
      "description": "Especialización en Google Maps, Bing Maps y posicionamiento local para negocios físicos"
    }
  ]
};

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

const breadcrumbSchema = buildBreadcrumb([{ name: "Sobre nosotros", path: "/sobre-nosotros" }]);

export const Route = createFileRoute("/sobre-nosotros")({
  head: () => ({
    meta: [
      { title: "Sobre Nosotros | SEOEVOLUTION - Agencia SEO con IA" },
      {
        name: "description",
        content:
          "20+ años de experiencia en SEO. 400+ clientes. Primera agencia SEO con IA en España. Partners de Shopify y Lovable.",
      },
      {
        name: "keywords",
        content:
          "agencia seo españa, seoevolution, seo inteligencia artificial, shopify partner, agencia seo madrid, angel ruz serrano, mejor agencia seo",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl },
      { property: "og:title", content: "Sobre SEOEVOLUTION | Agencia SEO con IA" },
      {
        property: "og:description",
        content:
          "20+ años de experiencia en SEO. 400+ clientes. Primera agencia SEO con IA en España. Partners de Shopify y Lovable.",
      },
      { name: "twitter:title", content: "Sobre SEOEVOLUTION | Agencia SEO con IA" },
      {
        name: "twitter:description",
        content:
          "20+ años de experiencia en SEO. 400+ clientes. Primera agencia SEO con IA en España.",
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
    scripts: [
      ldJson(breadcrumbSchema),
      // Organization se emite una sola vez en __root (src/lib/site-schema.ts).
      { type: "application/ld+json", children: JSON.stringify(aboutPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqPageSchema) },
    ],
  }),
  component: SobreNosotros,
});
